import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarIcon, ImageIcon, Trash2, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { LinkCombobox } from "@/components/LinkCombobox";
import { MarkdownField } from "@/components/MarkdownField";
import { api, errText } from "@/lib/api";
import { isoDate, longDate, parseISO } from "@/lib/eventMeta";
import { isHex, readableOn } from "@/lib/forumColors";
import { DEFAULT_PAPER, PAPER_PALETTE, PRIORITIES, TEXTURES, paperStyle } from "@/lib/postItStyles";

const blank = () => ({
  body: "",
  title: "",
  color: DEFAULT_PAPER,
  texture: "plain",
  priority: "normal",
  pinned: false,
  expires_on: null,
  matter_id: null,
  event_id: null,
  todo_id: null,
});

export const PostItEditor = ({ open, onOpenChange, note, links, onSaved, imageUrl }) => {
  const [form, setForm] = useState(blank());
  const [custom, setCustom] = useState("");
  const [pendingImage, setPendingImage] = useState(null);
  const [imageCleared, setImageCleared] = useState(false);
  const [saving, setSaving] = useState(false);
  const editing = note?.post_it_id;

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  useEffect(() => {
    if (!open) return;
    setForm(note?.post_it_id ? { ...blank(), ...note } : blank());
    setCustom("");
    setPendingImage(null);
    setImageCleared(false);
  }, [open, note]);

  const save = async () => {
    if (!form.body.trim()) return toast.error("Write something on the note first");
    setSaving(true);
    const payload = {
      body: form.body.trim(),
      title: (form.title || "").trim() || null,
      color: form.color,
      texture: form.texture,
      priority: form.priority,
      pinned: !!form.pinned,
      expires_on: form.expires_on || null,
      matter_id: form.matter_id || null,
      event_id: form.event_id || null,
      todo_id: form.todo_id || null,
    };
    try {
      const { data } = editing
        ? await api.put(`/postits/${note.post_it_id}`, payload)
        : await api.post("/postits", payload);
      if (pendingImage) {
        const body = new FormData();
        body.append("file", pendingImage);
        await api.post(`/postits/${data.post_it_id}/image`, body);
      }
      toast.success(editing ? "Note updated" : "Note pinned to the board");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setSaving(false);
    }
  };

  const dropImage = async () => {
    setPendingImage(null);
    if (!editing || !note.has_image || imageCleared) return;
    try {
      await api.delete(`/postits/${note.post_it_id}/image`);
      setImageCleared(true);
      toast.success("Background image removed — your other edits are still here");
      onSaved?.();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/postits/${note.post_it_id}`);
      toast.success("Note binned");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const preview = paperStyle(form.color, form.texture);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl rounded-sm max-h-[75vh] overflow-y-auto"
        data-testid="postit-editor"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">
            {editing ? "Edit note" : "New post-it"}
          </DialogTitle>
          <DialogDescription className="text-sm">
            A scrap of paper for anything — colour it, texture it, give it a shelf life and hang it
            off a matter, a hearing or a to-do.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div
            className="rounded-sm p-4 border border-border"
            style={preview}
            data-testid="postit-preview"
          >
            <Input
              data-testid="postit-title-input"
              value={form.title || ""}
              onChange={(e) => set({ title: e.target.value })}
              placeholder="Heading (optional)"
              className="rounded-sm h-11 text-base bg-transparent border-0 border-b border-current/30 px-0 font-heading font-bold placeholder:opacity-60 focus-visible:ring-0"
              style={{ color: "inherit" }}
            />
            <div className="mt-2">
              <MarkdownField
                value={form.body}
                onChange={(v) => set({ body: v })}
                testId="postit-body-input"
                minHeight={150}
                bare
                placeholder="Chase the registry on the caveat… ask Rohan for the 2019 compilation…"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="label-caps">Paper colour</Label>
            <div className="flex flex-wrap items-center gap-1.5" data-testid="postit-colors">
              {PAPER_PALETTE.map((hex) => (
                <button
                  key={hex}
                  type="button"
                  data-testid={`postit-color-${hex.replace("#", "")}`}
                  onClick={() => set({ color: hex })}
                  className="h-9 w-9 rounded-sm border border-border grid place-items-center transition-transform duration-200 hover:scale-110"
                  style={{ background: hex }}
                >
                  {form.color?.toUpperCase() === hex ? (
                    <span className="text-xs font-bold" style={{ color: readableOn(hex) }}>
                      ✓
                    </span>
                  ) : null}
                </button>
              ))}
              <Input
                data-testid="postit-color-custom"
                value={custom}
                onChange={(e) => {
                  setCustom(e.target.value);
                  if (isHex(e.target.value)) set({ color: e.target.value });
                }}
                placeholder="#RRGGBB"
                className="rounded-sm h-9 w-28 text-sm font-mono"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="label-caps">Texture</Label>
            <div className="flex flex-wrap gap-2" data-testid="postit-textures">
              {TEXTURES.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  data-testid={`postit-texture-${t.value}`}
                  onClick={() => set({ texture: t.value })}
                  className={`px-3 py-2 rounded-sm border text-sm font-medium transition-colors duration-200 ${
                    form.texture === t.value
                      ? "bg-primary text-primary-foreground border-primary"
                      : "border-border hover:bg-accent"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label className="label-caps">Priority</Label>
              <div className="grid grid-cols-3 gap-2">
                {PRIORITIES.map((p) => (
                  <button
                    key={p.value}
                    type="button"
                    data-testid={`postit-priority-${p.value}`}
                    onClick={() => set({ priority: p.value })}
                    className={`px-3 py-2.5 rounded-sm border text-sm font-semibold transition-colors duration-200 ${
                      form.priority === p.value
                        ? "bg-primary text-primary-foreground border-primary"
                        : "border-border hover:bg-accent"
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label className="label-caps">Expires on</Label>
              <div className="flex gap-2">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      data-testid="postit-expiry-trigger"
                      className="flex-1 justify-start rounded-sm h-11 text-base font-normal"
                    >
                      <CalendarIcon className="h-4 w-4 mr-2" />
                      {form.expires_on ? longDate(form.expires_on) : "No expiry"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={form.expires_on ? parseISO(form.expires_on) : undefined}
                      onSelect={(d) => d && set({ expires_on: isoDate(d) })}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                {form.expires_on ? (
                  <Button
                    variant="outline"
                    data-testid="postit-expiry-clear"
                    onClick={() => set({ expires_on: null })}
                    className="rounded-sm h-11 px-3"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                ) : null}
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between border border-border rounded-sm px-4 py-3">
            <div>
              <p className="text-base font-semibold">Pin to the top</p>
              <p className="text-sm text-muted-foreground">Pinned notes sit above everything else</p>
            </div>
            <Switch
              data-testid="postit-pinned-switch"
              checked={!!form.pinned}
              onCheckedChange={(v) => set({ pinned: v })}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="label-caps">Matter</Label>
              <LinkCombobox
                options={links.matters}
                value={form.matter_id}
                onChange={(v) => set({ matter_id: v })}
                testId="postit-link-matter"
                emptyLabel="No matter"
              />
            </div>
            <div className="space-y-2">
              <Label className="label-caps">Diary entry</Label>
              <LinkCombobox
                options={links.events}
                value={form.event_id}
                onChange={(v) => set({ event_id: v })}
                testId="postit-link-event"
                emptyLabel="No entry"
              />
            </div>
            <div className="space-y-2">
              <Label className="label-caps">To-do</Label>
              <LinkCombobox
                options={links.todos}
                value={form.todo_id}
                onChange={(v) => set({ todo_id: v })}
                testId="postit-link-todo"
                emptyLabel="No to-do"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="label-caps">Background image</Label>
            <div className="flex flex-wrap items-center gap-3">
              <label
                data-testid="postit-image-label"
                className="inline-flex items-center gap-2 h-11 px-5 rounded-full border border-border text-base font-medium cursor-pointer transition-colors duration-200 hover:bg-accent"
              >
                <ImageIcon className="h-5 w-5" />
                {pendingImage ? pendingImage.name.slice(0, 24) : "Choose an image"}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  data-testid="postit-image-input"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    e.target.value = "";
                    if (file) setPendingImage(file);
                  }}
                />
              </label>
              {note?.has_image && imageUrl && !imageCleared ? (
                <img
                  src={imageUrl}
                  alt="Note background"
                  className="h-11 w-16 object-cover rounded-sm border border-border"
                  data-testid="postit-image-thumb"
                />
              ) : null}
              {pendingImage || (note?.has_image && !imageCleared) ? (
                <Button
                  variant="outline"
                  data-testid="postit-image-remove"
                  onClick={dropImage}
                  className="rounded-full h-11 px-4"
                >
                  Remove image
                </Button>
              ) : null}
              <p className="text-xs text-muted-foreground">PNG, JPEG or WEBP up to 5 MB.</p>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          {editing ? (
            <Button
              variant="outline"
              onClick={remove}
              data-testid="postit-delete-btn"
              className="rounded-full h-11 mr-auto text-destructive border-destructive/40 hover:bg-destructive/10"
            >
              <Trash2 className="h-4 w-4 mr-2" /> Bin it
            </Button>
          ) : null}
          <Button
            variant="outline"
            className="rounded-full h-11"
            onClick={() => onOpenChange(false)}
            data-testid="postit-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            className="rounded-full h-11 px-6"
            onClick={save}
            disabled={saving}
            data-testid="postit-save-btn"
          >
            {saving ? "Saving…" : editing ? "Save note" : "Stick it up"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
